import { useState } from "react";
import {
  AlertTriangle,
  ArrowUpRight,
  Camera,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  Database,
} from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { SummaryCard } from "./SummaryCard";
import { CircularProgress, CompactScoreBar, ProgressBar } from "./progressBars";
import { getCategoryAccentClasses, getCategoryIcon } from "./categoryIcons";
import type {
  AssetCompletenessRow,
  SurveyCompletenessRow,
} from "./completenessHelpers";

type CompletenessView = "assets" | "survey";

interface DataCompletenessCardProps {
  overallCompletion: number | null;
  hasCompletenessData: boolean;
  surveyCompletenessRows: SurveyCompletenessRow[];
  visibleAssetCompletenessRows: AssetCompletenessRow[];
  completenessView: CompletenessView;
  setCompletenessView: (view: CompletenessView) => void;
  activeBelowTargetCount: number;
  activeBelowTargetLabel: string;
  surveyAttributesLoading: boolean;
  assetCompletenessLoading: boolean;
  completionDetails: Record<string, number> | null | undefined;
  onOpenAsset?: (assetId: string) => void;
  onOpenSurveyQuestion?: (fieldId: string) => void;
}

export function DataCompletenessCard({
  overallCompletion,
  hasCompletenessData,
  surveyCompletenessRows,
  visibleAssetCompletenessRows,
  completenessView,
  setCompletenessView,
  activeBelowTargetCount,
  activeBelowTargetLabel,
  surveyAttributesLoading,
  assetCompletenessLoading,
  completionDetails,
  onOpenAsset,
  onOpenSurveyQuestion,
}: DataCompletenessCardProps) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleRow = (key: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <SummaryCard
      icon={ClipboardCheck}
      iconColor="bg-emerald-500/15 text-emerald-400"
      tone={
        overallCompletion == null || overallCompletion >= 80
          ? "success"
          : overallCompletion >= 50
            ? "warning"
            : "danger"
      }
      title={
        <div>
          <div>Data Completeness</div>
          {hasCompletenessData && (
            <div className="mt-0.5 text-[10px] font-normal text-muted-foreground">
              {surveyCompletenessRows.length} survey sections · {visibleAssetCompletenessRows.length} asset categories
            </div>
          )}
        </div>
      }
      action={
        hasCompletenessData ? (
          <div className="flex items-center gap-2">
            {overallCompletion != null && <CircularProgress percent={overallCompletion} />}
          </div>
        ) : null
      }
    >
      <div className="flex min-h-[620px] w-full flex-col">
        {surveyAttributesLoading || assetCompletenessLoading ? (
          <div className="space-y-1.5">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex items-center gap-2 px-2 py-1.5 rounded-md border border-border/50 bg-card/50">
              <div className="h-3 w-3 rounded bg-muted animate-pulse shrink-0" />
              <div className="h-3 flex-1 rounded bg-muted animate-pulse" />
              <div className="h-3 w-12 rounded bg-muted animate-pulse shrink-0" />
            </div>
          ))}
          </div>
        ) : hasCompletenessData ? (
          <div className="flex flex-1 flex-col space-y-3">
          <div>
            <div className="grid grid-cols-2 rounded-lg border border-border/40 bg-background/30 p-1">
              <button
                type="button"
                onClick={() => setCompletenessView("survey")}
                className={cn(
                  "rounded-md px-3 py-2 text-xs font-semibold transition-colors",
                  completenessView === "survey"
                    ? "bg-amber-500/15 text-amber-300 shadow-inner ring-1 ring-amber-500/20"
                    : "text-muted-foreground hover:bg-muted/30"
                )}
              >
                Survey <span className="ml-1 text-[10px] opacity-70">{surveyCompletenessRows.length}</span>
              </button>
              <button
                type="button"
                onClick={() => setCompletenessView("assets")}
                className={cn(
                  "rounded-md px-3 py-2 text-xs font-semibold transition-colors",
                  completenessView === "assets"
                    ? "bg-amber-500/15 text-amber-300 shadow-inner ring-1 ring-amber-500/20"
                    : "text-muted-foreground hover:bg-muted/30"
                )}
              >
                Assets <span className="ml-1 text-[10px] opacity-70">{visibleAssetCompletenessRows.length}</span>
              </button>
            </div>
            <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">
              {completenessView === "assets" ? (
                <>
                  <Database className="mr-1 inline h-3 w-3" />
                  Data = assets with model # & manufacturer ·{" "}
                  <Camera className="mx-1 inline h-3 w-3" />
                  Photos = assets with a nameplate photo
                </>
              ) : (
                "% of survey questions answered in each section"
              )}
            </p>
            {activeBelowTargetCount > 0 && (
              <p className="mt-2 flex items-center gap-1.5 text-xs font-medium text-amber-400">
                <AlertTriangle className="h-3.5 w-3.5" />
                {activeBelowTargetCount}{" "}
                {activeBelowTargetCount === 1 ? activeBelowTargetLabel : `${activeBelowTargetLabel}s`} below 90%
                <span className="font-normal text-muted-foreground">
                  · tap a flagged row to see what's missing
                </span>
              </p>
            )}
          </div>

          {completenessView === "assets" && (
            <div className="grid grid-cols-[auto_auto_minmax(0,1fr)_auto_auto] items-center gap-3 px-2.5 text-xs uppercase tracking-wide text-muted-foreground/70">
              <span className="h-4 w-4" />
              <span />
              <span />
              <span className="flex w-[176px] items-center gap-1.5">
                <span className="h-3.5 w-3.5" />
                <span className="flex w-[156px] items-center gap-2">
                  <span className="flex-1 text-center">Data</span>
                  <span className="w-10" />
                </span>
              </span>
              <span className="flex w-[176px] items-center gap-1.5">
                <span className="h-3.5 w-3.5" />
                <span className="flex w-[156px] items-center gap-2">
                  <span className="flex-1 text-center">Photos</span>
                  <span className="w-10" />
                </span>
              </span>
            </div>
          )}

          <div className="flex-1 space-y-2 overflow-y-auto pr-1">
            {completenessView === "assets"
              ? visibleAssetCompletenessRows.map((item) => {
                const rowKey = `assets:${item.categoryId}`;
                const hasFlagged = item.flaggedAssets.length > 0;
                const isExpanded = hasFlagged && expandedRows.has(rowKey);
                return (
                  <div key={item.categoryId}>
                    <div
                      role={hasFlagged ? "button" : undefined}
                      tabIndex={hasFlagged ? 0 : undefined}
                      onClick={hasFlagged ? () => toggleRow(rowKey) : undefined}
                      onKeyDown={hasFlagged ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          toggleRow(rowKey);
                        }
                      } : undefined}
                      className={cn(
                        "grid grid-cols-[auto_auto_minmax(0,1fr)_auto_auto] items-center gap-3 rounded-md px-2.5 py-3 transition-colors hover:bg-muted/20",
                        hasFlagged && "cursor-pointer"
                      )}
                    >
                      <span className="flex h-4 w-4 items-center justify-center text-muted-foreground">
                        {hasFlagged && (
                          isExpanded
                            ? <ChevronDown className="h-4 w-4" />
                            : <ChevronRight className="h-4 w-4" />
                        )}
                      </span>
                      <div className={cn("flex h-8 w-8 items-center justify-center rounded-md border", getCategoryAccentClasses(item.name))}>
                        {(() => {
                          const Icon = getCategoryIcon(item.name);
                          return <Icon className="h-4 w-4" />;
                        })()}
                      </div>
                      <span className="min-w-0 truncate text-sm font-medium text-foreground">
                        {item.name}
                      </span>
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <Database className="h-3.5 w-3.5" />
                        <CompactScoreBar score={item.dataScore} />
                      </div>
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <Camera className="h-3.5 w-3.5" />
                        <CompactScoreBar score={item.photosScore} />
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="mb-1 mt-0.5 space-y-1 pl-9">
                        {item.flaggedAssets.map((asset) => (
                          <button
                            key={asset.id}
                            type="button"
                            onClick={() => onOpenAsset?.(asset.id)}
                            className="flex w-full items-center gap-2 rounded-md border border-border/40 bg-muted/20 px-2.5 py-2 text-left transition-colors hover:bg-muted/40"
                          >
                            {(() => {
                              const Icon = getCategoryIcon(item.name);
                              return <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />;
                            })()}
                            <span className="min-w-0 flex-1 truncate text-sm text-foreground">
                              {asset.name}
                            </span>
                            {asset.missingData && (
                              <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-[11px] font-medium text-amber-400">
                                <Database className="h-3 w-3" />
                                No model # / mfr
                              </span>
                            )}
                            {asset.missingPhoto && (
                              <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-[11px] font-medium text-amber-400">
                                <Camera className="h-3 w-3" />
                                No nameplate photo
                              </span>
                            )}
                            <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
              : surveyCompletenessRows.map((item) => {
                const rowKey = `survey:${item.id}`;
                const hasFlagged = item.unansweredQuestions.length > 0;
                const isExpanded = hasFlagged && expandedRows.has(rowKey);
                return (
                  <div key={item.id}>
                    <div
                      role={hasFlagged ? "button" : undefined}
                      tabIndex={hasFlagged ? 0 : undefined}
                      onClick={hasFlagged ? () => toggleRow(rowKey) : undefined}
                      onKeyDown={hasFlagged ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          toggleRow(rowKey);
                        }
                      } : undefined}
                      className={cn(
                        "grid grid-cols-[auto_auto_minmax(0,1fr)_auto] items-center gap-3 rounded-md px-2.5 py-3 transition-colors hover:bg-muted/20",
                        hasFlagged && "cursor-pointer"
                      )}
                    >
                      <span className="flex h-4 w-4 items-center justify-center text-muted-foreground">
                        {hasFlagged && (
                          isExpanded
                            ? <ChevronDown className="h-4 w-4" />
                            : <ChevronRight className="h-4 w-4" />
                        )}
                      </span>
                      <div className={cn("flex h-8 w-8 items-center justify-center rounded-md border", getCategoryAccentClasses(item.name))}>
                        {(() => {
                          const Icon = getCategoryIcon(item.name);
                          return <Icon className="h-4 w-4" />;
                        })()}
                      </div>
                      <span className="min-w-0 truncate text-sm font-medium text-foreground">
                        {item.name}
                      </span>
                      <CompactScoreBar score={item.score} widthClassName="w-[348px]" />
                    </div>
                    {isExpanded && (
                      <div className="mb-1 mt-0.5 space-y-1 pl-9">
                        {item.unansweredQuestions.map((question) => (
                          <button
                            key={question.id}
                            type="button"
                            onClick={() => onOpenSurveyQuestion?.(question.id)}
                            className="flex w-full items-center gap-2 rounded-md border border-border/40 bg-muted/20 px-2.5 py-2 text-left transition-colors hover:bg-muted/40"
                          >
                            {(() => {
                              const Icon = getCategoryIcon(item.name);
                              return <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />;
                            })()}
                            <span className="min-w-0 flex-1 truncate text-sm text-foreground">
                              {question.title}
                            </span>
                            <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-[11px] font-medium text-amber-400">
                              Unanswered
                            </span>
                            <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
          </div>
        ) : completionDetails && Object.keys(completionDetails).length > 0 ? (
          <div className="space-y-2.5">
          {Object.entries(completionDetails).map(([label, pct]) => (
            <div key={label} className="space-y-1">
              <span className="text-[10px] text-muted-foreground">{label}</span>
              <ProgressBar percent={typeof pct === "number" ? pct : 0} />
            </div>
          ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground/60 italic">No completeness data configured for this project.</p>
        )}
      </div>
    </SummaryCard>
  );
}
